// Parsed Markdown (parse.ts) as React elements, never HTML: a skill's file preview and an agent's
// replies. Code blocks and tables scroll sideways inside their own box, so a long line never
// widens the column they sit in.
import type { ReactNode } from "react";

import { cn } from "../cn";
import { type Block, type Inline, type ListItem, parseMarkdown } from "./parse";

function Text({ parts }: { parts: Inline[] }): ReactNode {
  return parts.map((p, i) => {
    switch (p.kind) {
      case "text":
        return <span key={i}>{p.text}</span>;
      case "code":
        return (
          <code key={i} className="rounded bg-gray-900/[0.07] px-1 py-0.5 font-mono text-[0.9em] [overflow-wrap:anywhere] dark:bg-gray-0/10">
            {p.text}
          </code>
        );
      case "strong":
        return (
          <strong key={i} className="font-semibold">
            {p.text}
          </strong>
        );
      case "em":
        return <em key={i}>{p.text}</em>;
      case "link":
        return (
          <a key={i} href={p.href} target="_blank" rel="noreferrer noopener" className="text-primary underline [overflow-wrap:anywhere]">
            {p.text}
          </a>
        );
    }
  });
}

const HEADING = ["text-xl", "text-lg", "text-base", "text-[15px]", "text-sm", "text-sm"];

function List({ block }: { block: Extract<Block, { kind: "list" }> }) {
  const Tag = block.ordered ? "ol" : "ul";
  return (
    <Tag className={cn(block.ordered ? "list-decimal" : "list-disc", "m-0 space-y-1 ps-5")}>
      {block.items.map((item: ListItem, i) => (
        <li key={i} className="whitespace-pre-line">
          <Text parts={item.text} />
          {item.children ? (
            <div className="mt-1">
              <List block={item.children} />
            </div>
          ) : null}
        </li>
      ))}
    </Tag>
  );
}

function BlockView({ block, frontMatter }: { block: Block; frontMatter?: string }) {
  switch (block.kind) {
    case "front":
      return (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-muted bg-gray-50 p-3 text-[13px]" aria-label={frontMatter}>
          {block.entries.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="font-mono text-gray-500">{k}</dt>
              <dd className="m-0 text-gray-800">{v}</dd>
            </div>
          ))}
        </dl>
      );
    case "heading": {
      const Tag = `h${block.level}` as const;
      return (
        <Tag className={cn(HEADING[block.level - 1], "m-0 font-semibold text-gray-900")}>
          <Text parts={block.text} />
        </Tag>
      );
    }
    case "paragraph":
      return (
        <p className="m-0 whitespace-pre-line break-words">
          <Text parts={block.text} />
        </p>
      );
    case "quote":
      return (
        <blockquote className="m-0 whitespace-pre-line border-s-4 border-muted ps-3 italic text-gray-600">
          <Text parts={block.text} />
        </blockquote>
      );
    case "list":
      return <List block={block} />;
    case "code":
      return (
        <pre
          data-lang={block.lang || undefined}
          className="custom-scrollbar m-0 max-w-full overflow-x-auto rounded-lg border border-muted bg-gray-50 p-3 font-mono text-[12.5px] leading-relaxed text-gray-800 dark:bg-gray-100"
        >
          <code>{block.text}</code>
        </pre>
      );
    case "table":
      return (
        <div className="custom-scrollbar max-w-full overflow-x-auto rounded-lg border border-muted">
          <table className="w-full border-collapse text-start text-[12.5px]">
            <thead className="bg-gray-50 dark:bg-gray-100">
              <tr>
                {block.head.map((cell, i) => (
                  <th key={i} className="border-b border-muted px-2.5 py-1.5 text-start font-semibold">
                    <Text parts={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r} className="border-b border-muted last:border-b-0">
                  {row.map((cell, c) => (
                    <td key={c} className="px-2.5 py-1.5 align-top">
                      <Text parts={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "rule":
      return <hr className="m-0 border-muted" />;
  }
}

interface MarkdownProps {
  source: string;
  /** Read a leading `---` block as front matter, labelled with this text (a skill's SKILL.md). */
  frontMatter?: string;
  /** Shown when the source has nothing to render. */
  empty?: ReactNode;
  className?: string;
  label?: string;
}

export function Markdown({ source, frontMatter, empty, className, label }: MarkdownProps) {
  const blocks = parseMarkdown(source, { frontMatter: frontMatter !== undefined });
  return (
    <div className={cn("min-w-0 space-y-2.5 text-[13.5px] leading-relaxed", className)} role={label ? "article" : undefined} aria-label={label} data-testid="markdown">
      {blocks.length === 0 ? empty : null}
      {blocks.map((b, i) => (
        <BlockView key={i} block={b} frontMatter={frontMatter} />
      ))}
    </div>
  );
}
